<template>
  <PeopleAndGroupsTemplate
    tab="pending"
    :selectedLength="selected.length"
    :clearSelected="clearSelected"
    @update-search="handleSearchUpdate"
  >
    <template #selected-count>
      {{ selected.length }}
      {{ selected.length === 1 ? "Applicant" : "Applicants" }} Selected
    </template>

    <template #actions>
      <template v-if="canCreateUser">
        <Button
          text
          small
          @click="approveAll"
          :disabled="!selected.length"
          :loading="loading"
          title="Approve selected users"
        >
          Approve All
        </Button>

        <Button
          text
          small
          @click="denyAll"
          :disabled="!selected.length"
          :loading="loading"
          title="Deny selected users"
        >
          Deny All
        </Button>
      </template>
    </template>

    <template #data-table>
      <PendingUsersDataTable
        :selected="selected"
        :pendingUsers="pendingUsers"
        :options="options"
        :total="total"
        :canCreateUser="canCreateUser"
        :onApproveClick="onApproveClick"
        :onDenyClick="onDenyClick"
        :onEditClick="onEditClick"
        :itemsPerPage="options.itemsPerPage"
        :footerProps="footerProps"
        :loading="loading"
        @update:selected="selected = $event"
        @update:options="handleOptionsUpdate"
        @checkbox-click="handleCheckboxClick"
      />
    </template>
    <template #overlay>
      <v-navigation-drawer v-model="showUserDialog" temporary fixed right width="500">
        <UserCreatorWidget
          v-if="showUserDialog"
          @close="closeUserDialog"
          @get-users="userCreated"
          :clientId="globalClientId"
          :editUserObject="selectedPendingUser"
          source="PENDING_USERS"
        />
      </v-navigation-drawer>
    </template>
  </PeopleAndGroupsTemplate>
</template>

<script>
import PeopleAndGroupsTemplate from "@/components/people-and-groups/PeopleAndGroupsTemplate.vue";
import PendingUsersDataTable from "@/components/pending-users/PendingUsersDataTable.vue";
import Button from "@/components/generic/Button.vue";
import UserService from "@/services/UserService";
import OnboardingService from "@/services/OnboardingService";
import UserCreatorWidget from "@/components/people/UserCreatorWidgetV2.vue";
import {
  firstPointsMembershipMessage,
  pointsMembershipMessageFromError,
} from "@/utils/pointsMembership";
import { mapState } from "vuex";
import moment from "moment";

export default {
  name: "PendingUsers",
  title: "Pending",
  components: {
    PeopleAndGroupsTemplate,
    PendingUsersDataTable,
    Button,
    UserCreatorWidget,
  },
  data() {
    return {
      selected: [],
      pendingUsers: [],
      total: 0,
      loading: false,
      search: "",
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      footerProps: {
        "items-per-page-options": [10, 25, 50, 100],
      },
      showUserDialog: false,
      selectedPendingUser: null,
      selectedPendingUserId: null,
    };
  },
  computed: {
    ...mapState(["globalClientId", "permissions"]),
    canCreateUser() {
      return (this.permissions || []).includes("users:create:user");
    },
  },
  methods: {
    resetState() {
      this.selected = [];
      this.pendingUsers = [];
      this.total = 0;
      this.loading = false;
      this.search = "";
      this.options = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      };
      this.showUserDialog = false;
      this.selectedPendingUser = null;
    },
    handleOptionsUpdate(newOptions) {
      this.options = { ...newOptions };
      this.getPendingUsers();
    },
    async approveAll() {
      await this.approvePendingUsers(this.selected);
    },
    async denyAll() {
      await this.denyPendingUsers(this.selected.map((x) => x.id));
    },
    async getPendingUsers() {
      this.loading = true;
      try {
        const params = {
          status: "Pending",
          clientId: this.globalClientId,
          limit: this.options.itemsPerPage,
          page: this.options.page,
          search: this.search,
        };

        // Add sort params if sorting is applied

        if (this.options.sortBy && this.options.sortBy.length > 0) {
          let sortBy = this.options.sortBy[0];
          // Sort by lastName when displayName column is selected
          if (sortBy === "displayName") {
            sortBy = "lastName";
          }
          params.sortBy = sortBy;
          params.sortOrder = this.options.sortDesc[0] ? "desc" : "asc";
        }

        const response = await UserService.getPendingUsers(params);
        this.pendingUsers = response.rows;
        this.total = response.totalCount;
        // Set the count directly from the response to avoid an extra API call
        this.$store.dispatch("setPendingUserCount", response.totalCount);
      } catch (error) {
        console.error("Error getting pending users", error);
        this.$store.dispatch("showError", {
          title: "Error",
          message: "Failed to get pending users",
        });
      } finally {
        this.loading = false;
      }
    },
    async userCreated(user) {
      this.loading = true;
      await this.approvePendingUser(user);
      await this.getPendingUsers();
    },
    handleCheckboxClick(item) {
      const index = this.selected.findIndex((x) => x.id === item.id);
      if (index > -1) {
        this.selected.splice(index, 1);
      } else {
        this.selected.push(item);
      }
    },
    clearSelected() {
      this.selected = [];
    },
    handleSearchUpdate(val) {
      this.search = val;
      this.options.page = 1;
      this.getPendingUsers();
    },
    async denyPendingUsers(pendingUserIds) {
      console.log("--- DENY PENDING USERS ---", pendingUserIds);
      if (!Array.isArray(pendingUserIds)) {
        pendingUserIds = [pendingUserIds];
      }
      try {
        this.loading = true;
        const response = await UserService.updatePendingUserStatus({
          pendingUserIds,
          status: "Denied",
        });
        if (response.success) {
          console.log("Deny pending users response ", response);
          this.$store.dispatch("showSuccess", {
            title: "Success",
            message: `Pending users denied successfully (${response.count})`,
          });
          await this.getPendingUsers();
          this.selected = this.selected.filter((user) => !pendingUserIds.includes(user.id));
        } else {
          throw new Error("Failed to deny pending users");
        }
      } catch (error) {
        this.$store.dispatch("showError", {
          title: "Error",
          message: error.response.data.message || "Failed to deny pending users",
        });
      } finally {
        this.loading = false;
      }
    },
    async approvePendingUsers(pendingUsers) {
      if (!Array.isArray(pendingUsers) || pendingUsers.length === 0) {
        return;
      }
      try {
        this.loading = true;

        const body = pendingUsers.map((user) => {
          const smsPasswordlessEnabled = !!user.businessPhone;
          return {
            ...user.additionalFields,
            firstName: user.firstName,
            lastName: user.lastName,
            businessEmail: user.businessEmail,
            businessCountry: user.businessCountry,
            businessPhone: user.businessPhone,
            organization: user.organization,
            jobTitle: user.jobTitle,
            passwordEnabled: smsPasswordlessEnabled ? false : true,
            smsPasswordlessEnabled,
            clientId: user.clientId,
            scheduledOnboardingDate: moment().format("YYYY-MM-DDTHH:mm"),
          };
        });
        const response = await OnboardingService.createUserV2(body, { wait: "true" });
        const membershipError = firstPointsMembershipMessage(response?.result);
        if (membershipError) {
          this.$store.dispatch("showError", {
            title: "Error",
            message: membershipError,
          });
          await this.getPendingUsers();
          this.selected = this.selected.filter((user) =>
            this.pendingUsers.some((pending) => pending.id === user.id)
          );
          return;
        }
        this.$store.dispatch("showSuccess", {
          title: "Success",
          message: `Successfully approved ${pendingUsers.length} pending user(s)`,
        });
        await this.getPendingUsers();
        this.selected = this.selected.filter((user) => !pendingUsers.some((p) => p.id === user.id));
      } catch (error) {
        console.error("Error approving pending users", error);
        const membershipError = pointsMembershipMessageFromError(error);
        this.$store.dispatch("showError", {
          title: "Error",
          message: membershipError || error.message || "Failed to approve pending users",
        });
      } finally {
        this.loading = false;
      }
    },
    async approvePendingUser(createdUser) {
      const needToUpdateStatus =
        (this.selectedPendingUser.businessEmail &&
          this.selectedPendingUser.businessEmail !== createdUser.businessEmail) ||
        (this.selectedPendingUser.businessPhone &&
          this.selectedPendingUser.businessPhone !== createdUser.businessPhone);

      if (needToUpdateStatus) {
        // callback for userCreatorWidget in case admin edits PAX info. need to update status to approved since onboarding service queries by email or phone number
        const id = this.selectedPendingUserId;
        await UserService.updatePendingUserStatus({ pendingUserIds: [id], status: "Approved" });
      }
    },
    async onDenyClick(item) {
      await this.denyPendingUsers(item.id);
    },
    async onApproveClick(item) {
      await this.approvePendingUsers([item]);
    },
    onEditClick(item) {
      const smsPasswordlessEnabled = !!item.businessPhone;
      this.selectedPendingUser = {
        ...item.additionalFields,
        firstName: item.firstName,
        lastName: item.lastName,
        businessEmail: item.businessEmail,
        businessCountry: item.businessCountry,
        businessPhone: item.businessPhone,
        organization: item.organization,
        jobTitle: item.jobTitle,
        passwordEnabled: smsPasswordlessEnabled ? false : true,
        smsPasswordlessEnabled,
        clientId: item.clientId,
        scheduledOnboardingDate: moment().format("YYYY-MM-DDTHH:mm"),
      };
      this.selectedPendingUserId = item.id;
      this.showUserDialog = true;
    },
    closeUserDialog() {
      this.showUserDialog = false;
      this.selectedPendingUser = null;
      this.selectedPendingUserId = null;
    },
  },
  watch: {
    globalClientId: {
      handler() {
        this.resetState();
        this.getPendingUsers();
      },
    },
  },
};
</script>

<style scoped></style>
